import { useState } from "react";
import { adminApi } from "../api/admin";
import type { Collection, Member, Visibility } from "../api/types";
import { VISIBILITY_OPTIONS } from "../lib/format";
import { parseTags, titleFromFilename } from "./formUtils";
import { uploadPhotoFile, uploadVideoFile } from "./mediaTools";

type Kind = "photo" | "video";

interface Job {
  key: string;
  label: string;
  status: "waiting" | "working" | "done" | "failed";
  message?: string;
  run: () => Promise<void>;
}

/**
 * Add many photos or videos in one go: pick files (and, for videos, paste
 * links, one per line), choose the settings they should all share, and watch
 * each one go in. Every item gets a title from its file name — or, for a link,
 * from the platform — to be tidied up afterwards in the table.
 *
 * Items are created one at a time, so a failure (an unsupported file, a dead
 * link) is reported against that item and the rest carry on.
 */
export function MediaImport({
  kind,
  collections,
  members,
  onClose,
}: {
  kind: Kind;
  collections: Collection[];
  members: Member[];
  /** Called when the dialog closes; `changed` is true if anything was added. */
  onClose: (changed: boolean) => void;
}) {
  const [files, setFiles] = useState<File[]>([]);
  const [links, setLinks] = useState("");
  const [collection, setCollection] = useState(collections[0]?.id ?? "life");
  const [tags, setTags] = useState("");
  const [visibility, setVisibility] = useState<Visibility>("PUBLIC");
  const [sharedWith, setSharedWith] = useState<string[]>([]);
  const [featured, setFeatured] = useState(false);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");

  const noun = kind === "photo" ? "photos" : "videos";
  const linkList = links.split(/\s+/).map((l) => l.trim()).filter(Boolean);
  const started = jobs.length > 0;
  const finished = started && !running;
  const added = jobs.filter((j) => j.status === "done").length;

  function shared() {
    return {
      collection,
      tags: parseTags(tags),
      visibility,
      sharedWith: visibility === "RESTRICTED" ? sharedWith : [],
      featured,
    };
  }

  function buildJobs(): Job[] {
    const common = shared();
    const fileJobs: Job[] = files.map((file, i) => ({
      key: `f${i}-${file.name}`,
      label: file.name,
      status: "waiting",
      run: async () => {
        const title = titleFromFilename(file.name);
        if (kind === "photo") {
          const up = await uploadPhotoFile(file);
          await adminApi.createPhoto({ title, ...up, ...common });
        } else {
          const up = await uploadVideoFile(file);
          await adminApi.createVideo({ title, ...up, provider: "FILE", ...common });
        }
      },
    }));
    const linkJobs: Job[] = linkList.map((url, i) => ({
      key: `l${i}-${url}`,
      label: url,
      status: "waiting",
      run: async () => {
        const info = await adminApi.lookupVideo(url);
        await adminApi.createVideo({
          title: info.title ?? url,
          description: info.description,
          url: info.url,
          thumbnailUrl: info.thumbnailUrl,
          provider: info.provider,
          durationSeconds: info.durationSeconds,
          publishedAt: info.publishedAt,
          ...common,
        });
      },
    }));
    return [...fileJobs, ...linkJobs];
  }

  async function start() {
    if (visibility === "RESTRICTED" && sharedWith.length === 0) {
      setError("Pick at least one member to share with.");
      return;
    }
    const queue = buildJobs();
    if (queue.length === 0) {
      setError(kind === "photo" ? "Choose some photos first." : "Choose some files or paste some links first.");
      return;
    }
    setError("");
    setJobs(queue);
    setRunning(true);
    for (const job of queue) {
      setJobs((prev) => prev.map((j) => (j.key === job.key ? { ...j, status: "working" } : j)));
      try {
        await job.run();
        setJobs((prev) => prev.map((j) => (j.key === job.key ? { ...j, status: "done" } : j)));
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed.";
        setJobs((prev) =>
          prev.map((j) => (j.key === job.key ? { ...j, status: "failed", message } : j)),
        );
      }
    }
    setRunning(false);
  }

  return (
    <div
      className="admin-modal__backdrop"
      role="dialog"
      aria-modal="true"
      onMouseDown={() => !running && onClose(added > 0)}
    >
      <div className="admin-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="admin-modal__head">
          <h2>{kind === "photo" ? "Upload photos" : "Add videos"}</h2>
        </div>
        <div className="admin-modal__body">
          {error && (
            <p className="notice notice--err" role="alert">
              ✕ {error}
            </p>
          )}

          {!started && (
            <div className="form" style={{ maxWidth: "none" }}>
              <div className="field">
                <label htmlFor="import-files">{kind === "photo" ? "Photos" : "Video files"}</label>
                <input
                  id="import-files"
                  type="file"
                  multiple
                  accept={kind === "photo" ? "image/*" : "video/*"}
                  onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
                />
                <span className="field__help">
                  {files.length
                    ? `${files.length} file(s) chosen.`
                    : kind === "photo"
                      ? "Each is uploaded with a thumbnail made in your browser, and titled from its file name."
                      : "Each is uploaded with a poster frame, and titled from its file name."}
                </span>
              </div>

              {kind === "video" && (
                <div className="field">
                  <label htmlFor="import-links">…or links (one per line)</label>
                  <textarea
                    id="import-links"
                    value={links}
                    placeholder={"https://youtu.be/…\nhttps://vimeo.com/…"}
                    onChange={(e) => setLinks(e.target.value)}
                  />
                  <span className="field__help">
                    YouTube, Vimeo, Dailymotion, Facebook, TikTok, Loom, and Streamable play in the page
                    (title and thumbnail are fetched for you); any other link is kept as a link out.
                  </span>
                </div>
              )}

              <fieldset className="import__shared">
                <legend>Applied to every {kind}</legend>
                <div className="field">
                  <label htmlFor="import-collection">Collection</label>
                  <select id="import-collection" value={collection} onChange={(e) => setCollection(e.target.value)}>
                    {collections.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="import-tags">Tags</label>
                  <input
                    id="import-tags"
                    type="text"
                    value={tags}
                    placeholder="Comma-separated, e.g. family, 2024"
                    onChange={(e) => setTags(e.target.value)}
                  />
                </div>
                <div className="field">
                  <label htmlFor="import-visibility">Who can see them</label>
                  <select
                    id="import-visibility"
                    value={visibility}
                    onChange={(e) => setVisibility(e.target.value as Visibility)}
                  >
                    {VISIBILITY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                {visibility === "RESTRICTED" && (
                  <div className="field">
                    <label>Shared with</label>
                    {members.length === 0 ? (
                      <p className="checklist__empty">No members yet — add people on the Members tab first.</p>
                    ) : (
                      <div className="checklist">
                        {members.map((m) => (
                          <label key={m.id} className="checklist__item">
                            <input
                              type="checkbox"
                              style={{ width: "auto" }}
                              checked={sharedWith.includes(m.email)}
                              onChange={(e) =>
                                setSharedWith((prev) =>
                                  e.target.checked ? [...prev, m.email] : prev.filter((x) => x !== m.email),
                                )
                              }
                            />{" "}
                            {m.displayName ? `${m.displayName} — ${m.email}` : m.email}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <label className="checklist__item">
                  <input
                    type="checkbox"
                    style={{ width: "auto" }}
                    checked={featured}
                    onChange={(e) => setFeatured(e.target.checked)}
                  />{" "}
                  Put them on the front page
                </label>
              </fieldset>
            </div>
          )}

          {started && (
            <>
              <p className="mono" style={{ fontSize: "0.8rem" }}>
                {running
                  ? `Adding… ${jobs.filter((j) => j.status === "done" || j.status === "failed").length} of ${jobs.length}`
                  : `Added ${added} of ${jobs.length} ${noun}.`}
              </p>
              <ul className="import__jobs">
                {jobs.map((j) => (
                  <li key={j.key} data-status={j.status}>
                    <span className="import__status">{STATUS[j.status]}</span> {j.label}
                    {j.message && <span className="import__message"> — {j.message}</span>}
                  </li>
                ))}
              </ul>
            </>
          )}

          <div className="admin-actions">
            {!started && (
              <>
                <button type="button" className="btn btn--ghost" onClick={() => onClose(false)}>
                  Cancel
                </button>
                <button type="button" className="btn" onClick={() => void start()}>
                  {kind === "photo" ? "Upload" : "Add"}{" "}
                  {files.length + linkList.length || ""} {noun}
                </button>
              </>
            )}
            {finished && (
              <button type="button" className="btn" onClick={() => onClose(added > 0)}>
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const STATUS: Record<Job["status"], string> = {
  waiting: "·",
  working: "…",
  done: "✓",
  failed: "✕",
};
